import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportPlatformError } from '@/lib/platformLogs';

interface Props {
  children: ReactNode;
  /** Zone concernée (ex: « cette page »). */
  scope?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * ErrorBoundary — filet de sécurité global.
 *
 * Sans lui, la moindre erreur d'affichage démonte tout le React et laisse
 * l'utilisateur devant une page blanche (obligé de recharger). Ici, on capture
 * l'erreur et on affiche un message clair avec une action de reprise.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || 'Erreur inattendue' };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
    void reportPlatformError({
      level: 'error',
      category: 'render',
      source: 'react',
      message: error?.message || 'Erreur de rendu React',
      details: { stack: error?.stack, componentStack: info?.componentStack },
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, message: '' });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md text-center bg-background-50 border border-background-200/70 rounded-2xl p-8">
          <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-accent-600"></i>
          </div>
          <h2 className="text-lg font-bold font-heading text-foreground-950 mb-1.5">
            Un petit accroc…
          </h2>
          <p className="text-sm text-foreground-500 leading-relaxed mb-5">
            {this.props.scope ? `${this.props.scope} n'a pas pu s'afficher correctement. ` : ''}
            Réessayez, vos données sont conservées.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
            <button
              onClick={this.handleRetry}
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Réessayer
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
            >
              Recharger la page
            </button>
          </div>
          {this.state.message && (
            <p className="mt-5 text-[11px] text-foreground-400 break-words">{this.state.message}</p>
          )}
        </div>
      </div>
    );
  }
}